import { reduce } from "et3-type-guards";
import { camelCase, startCase } from "lodash";

import { EUserStatus } from "../../../../../../constants/enum/user-status";
import i18n from "../../../../../../constants/i18n";
import { getFirstKnownServerDate } from "../../../../../../helpers/et3-date-helper";
import ET3Div from "../../../../../../core/et3-div";
import { isBlankValue } from "../../../../../../core/et3-field-grid";
import { FIELD_GRID_EMPTY_VALUE } from "../../../../../../core/et3-field-grid/field-grid-constants";
import { withLiteObserverAndTheme } from "../../../../../../core/et3-enhanced-render";
import { getPermissionIDRequired } from "../../../../../../helpers/et3-has-permission";
import getCorePersonalStore from "../../../../../../stores/core/core-personal-store";
import getCorePopUpStore from "../../../../../../stores/core/core-popup-store";
import getInvestigationCasesStore from "../../../../../../stores/investigation-cases-store";
import getReportsStore from "../../../../../../stores/reports-store";
import getUserAdvancedStore from "../../../../../../stores/user-store/advanced-store";
import { MODERATION_ROW_GAP } from "../details-box-constants";
import ModerationRow from "./ModerationRow";
import {
  MODERATION_SOFT_CRITICAL,
  MODERATION_SOFT_INFO,
  MODERATION_SOFT_NEUTRAL,
  MODERATION_SOFT_POSITIVE,
  MODERATION_SOFT_WARNING
} from "./moderation-constants";

const UserStatus = withLiteObserverAndTheme((props) => {
  const { theme } = props;
  const { colors, unitY } = theme;
  const advancedStore = getUserAdvancedStore();
  const userId = advancedStore.currentUserId.get();
  const data = advancedStore.userData.get(userId);
  const isAnyKhattabaAdmin = getCorePersonalStore().isAnyKhattabaAdmin.get();

  const userStatus = reduce(() => data.status, "");
  const isActiveStatus = userStatus === EUserStatus.ACTIVE;
  const isUserTester = reduce(() => data.isTester, false);
  const isSilentBanned = advancedStore.isSilentBanned.get();
  const silentBannedOnRaw = reduce(() => `${data.silentBannedOn}`, "");
  const silentBannedOn = isBlankValue(silentBannedOnRaw)
    ? i18n.get("YES_VALUE")
    : silentBannedOnRaw;
  const khattabaAssignedRaw = reduce(() => `${data.khattabaAssigned}`, "");
  const khattabaAssigned = isBlankValue(khattabaAssignedRaw)
    ? FIELD_GRID_EMPTY_VALUE
    : khattabaAssignedRaw;
  const deactivatedOnRaw = reduce(() => `${data.deactivatedOn}`, "");
  const isDeactivatedStatus = userStatus === EUserStatus.DEACTIVATED;
  const statusDate = isDeactivatedStatus
    ? getFirstKnownServerDate([deactivatedOnRaw])
    : "";

  const {
    userIsUnderCsrInvestigation,
    userUnderInvestigationAndReadyToResolve
  } = advancedStore.currentUserIsUnderCsrInvestigation.get();
  const currentUserIsUnderCsrReport =
    advancedStore.currentUserIsUnderCsrReport.get();
  const isUnderCsrInvestigationEnabled =
    userIsUnderCsrInvestigation && !currentUserIsUnderCsrReport;
  const userPermissions = getCorePersonalStore().userPermissions.get();
  const hasInvestigationPermission =
    userPermissions.indexOf(getPermissionIDRequired("Investigation-Case")) > -1;
  const hasReportsPermission =
    userPermissions.indexOf(getPermissionIDRequired("Reports")) > -1;

  return (
    <ET3Div
      isScrollable={false}
      isExtendable={true}
      withShadow={false}
      width={0}
      height={0}
      fDirection="column"
      alignItems="stretch"
      justifyContent="flex-start"
      color={colors.all.transparent}
      style={{ width: "100%", gap: MODERATION_ROW_GAP * unitY }}
      onClick={() => {}}
    >
      <ModerationRow
        label={startCase(camelCase(userStatus))}
        value={statusDate}
        toneColor={isActiveStatus ? colors.all.green : colors.all.red}
        backgroundColor={
          isActiveStatus ? MODERATION_SOFT_POSITIVE : MODERATION_SOFT_CRITICAL
        }
        isActive={true}
        onPress={() => {}}
      />
      <ModerationRow
        label={i18n.get("SILENT_BANNED")}
        value={isSilentBanned ? silentBannedOn : i18n.get("NO_VALUE")}
        toneColor={colors.all.red}
        backgroundColor={
          isSilentBanned ? MODERATION_SOFT_CRITICAL : MODERATION_SOFT_NEUTRAL
        }
        isActive={isSilentBanned}
        onPress={() => {}}
      />
      {isAnyKhattabaAdmin ? null : (
        <ModerationRow
          label={i18n.get("UNDER_CSR_INVESTIGATION_STATUS")}
          value={i18n.get(
            userIsUnderCsrInvestigation ? "OPEN_CASE" : "NONE_VALUE"
          )}
          toneColor={colors.all.darkOrange}
          backgroundColor={
            userIsUnderCsrInvestigation
              ? MODERATION_SOFT_WARNING
              : MODERATION_SOFT_NEUTRAL
          }
          isActive={userIsUnderCsrInvestigation}
          onPress={() => {
            if (
              userIsUnderCsrInvestigation &&
              !userUnderInvestigationAndReadyToResolve
            ) {
              getCorePopUpStore().showAlertMessage(
                "USER_UNDER_CSR_INVESTIGATION_BUT_WILL_APPEAR_SOON"
              );
              return;
            }
            if (isUnderCsrInvestigationEnabled && hasInvestigationPermission) {
              getInvestigationCasesStore().getInvestigationCaseByID({
                userID: userId
              });
            }
          }}
        />
      )}
      {isAnyKhattabaAdmin ? null : (
        <ModerationRow
          label={i18n.get("REPORTED")}
          value={i18n.get(
            currentUserIsUnderCsrReport ? "OPEN_CASE" : "NO_OPEN_REPORT"
          )}
          toneColor={colors.all.blue}
          backgroundColor={
            currentUserIsUnderCsrReport
              ? MODERATION_SOFT_INFO
              : MODERATION_SOFT_NEUTRAL
          }
          isActive={currentUserIsUnderCsrReport}
          onPress={() => {
            if (currentUserIsUnderCsrReport && hasReportsPermission) {
              getReportsStore().getReportByUserID(userId);
            }
          }}
        />
      )}
      <ModerationRow
        label={i18n.get("TESTER_ACCOUNT")}
        value={i18n.get(isUserTester ? "YES_VALUE" : "NO_VALUE")}
        toneColor={colors.all.darkOrange}
        backgroundColor={
          isUserTester ? MODERATION_SOFT_WARNING : MODERATION_SOFT_NEUTRAL
        }
        isActive={isUserTester}
        onPress={() => {}}
      />
      <ModerationRow
        label={i18n.get("KHATTABA_ASSIGNED")}
        value={khattabaAssigned}
        toneColor={colors.all.brightCyan}
        backgroundColor={MODERATION_SOFT_NEUTRAL}
        isActive={false}
        onPress={() => {}}
      />
    </ET3Div>
  );
});
export default UserStatus;
